import React, { MouseEvent, ReactNode, TouchEvent, useEffect, useRef, useState } from 'react';

import { getCords, isMouseMoveEvent, populateSlidesOnMouseTouchMove } from './helper.ts';

export interface CarouselProps {
  children: ReactNode;
  slides: ReactNode;
  arrowLeftDisabled: ReactNode;
  arrowRightDisabled: ReactNode;
  arrowLeft: ReactNode;
  arrowRight: ReactNode;
  autoPlay: number;
  slideIndex: number;
  isInfinite: boolean;
  itemsToShow: number;
  dots: boolean;
  setSlideIndex: (number) => void;
}

const defaultTransition = 'transform 400ms ease-in-out';
const minimumTouchDrag = 80;

const Carousel: React.FC<CarouselProps> = (props) => {
  const {
    children,
    slides,
    slideIndex = 0,
    arrowLeft,
    arrowRight,
    arrowLeftDisabled,
    arrowRightDisabled,
    isInfinite = false,
    autoPlay,
    dots = true,
    setSlideIndex,
  } = props;
  const carouseItems = children || slides;
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const [itemWidth, setItemWidth] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(slideIndex);
  const [transform, setTransform] = useState(0);

  const totalItems = React.Children.count(carouseItems);

  let transformPlaceHolder = 0;
  let onMove = false;
  let initialX = 0;
  let lastX = 0;
  let scrollDirection = '';
  let initialY = 0;
  let setIntervalId = null;
  let isDragged = true;

  useEffect(() => {
    if (containerRef.current) {
      setItemWidth(containerRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    setCurrentSlide(slideIndex);
  }, [slideIndex]);

  useEffect(() => {
    if (autoPlay && totalItems > 1) {
      setIntervalId = setInterval(() => {
        setCurrentSlide((prevSlide) => {
          if (prevSlide >= totalItems - 1) {
            return 0;
          } else {
            return prevSlide + 1;
          }
        });
      }, autoPlay);
      return () => {
        clearInterval(setIntervalId);
        setIntervalId = null;
      };
    }
  }, [autoPlay]);

  useEffect(() => {
    if (transform !== transformPlaceHolder) {
      transformPlaceHolder = transform;
    }
  }, [transform]);

  useEffect(() => {
    const nextCarouselPosition = -itemWidth * currentSlide;
    setTransform(nextCarouselPosition);
    if (setSlideIndex) {
      setSlideIndex(currentSlide);
    }
  }, [currentSlide]);

  const correctItemsPosition = (itemWidth: number) => {
    const nextTransform = totalItems < 1 ? 0 : -(itemWidth * currentSlide);
    setTransformDirectly(nextTransform, true);
    setTransform(nextTransform);
  };
  const setTransformDirectly = (position: number, withAnimation?: boolean) => {
    transformPlaceHolder = position;
    const currentTransform = transformPlaceHolder || transform;
    if (listRef.current) {
      setAnimationDirectly(withAnimation);
      listRef.current.style.transform = `translate3d(${currentTransform}px,0,0)`;
    }
  };

  const setAnimationDirectly = (animationAllowed?: boolean) => {
    if (listRef.current) {
      if (animationAllowed) {
        listRef.current.style.transition = defaultTransition;
      } else {
        listRef.current.style.transition = 'none';
      }
    }
  };

  const next = () => {
    if (currentSlide + 2 <= totalItems) {
      setCurrentSlide((prevSlide) => prevSlide + 1);
    } else {
      if (!isInfinite) return;
      setCurrentSlide(0);
    }
  };

  const previous = () => {
    const previousItems = currentSlide - 1;
    if (previousItems >= 0) {
      setCurrentSlide(previousItems);
    } else {
      if (!isInfinite) return;
      const nextItems = totalItems - 1;
      setCurrentSlide(nextItems);
    }
  };

  const handleDown = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    const { clientX, clientY } = getCords(isMouseMoveEvent(e) ? e : e.touches[0]);
    onMove = true;
    isDragged = false;
    initialX = clientX;
    initialY = clientY;
    lastX = clientX;
  };

  const handleMove = (e: MouseEvent | TouchEvent) => {
    const { clientX, clientY } = getCords(isMouseMoveEvent(e) ? e : e.touches[0]);
    const diffX = initialX - clientX;
    const diffY = initialY - clientY;
    if (onMove) {
      if (!(Math.abs(diffX) > Math.abs(diffY))) {
        // return if vertical movement is detected
        return;
      }
      isDragged = true;
      const { direction, nextPosition, canContinue } = populateSlidesOnMouseTouchMove({
        props,
        itemWidth,
        totalItems,
        currentSlide,
        initialX,
        lastX,
        clientX,
        transformPlaceHolder,
      });
      if (direction) {
        scrollDirection = direction;
        if (canContinue && nextPosition !== undefined) {
          setTransformDirectly(nextPosition);
        }
      }
      lastX = clientX;
    }
  };

  const handleOut = () => {
    if (onMove) {
      setAnimationDirectly(true);
      if (scrollDirection === 'right') {
        const canGoNext = initialX - lastX >= minimumTouchDrag;
        if (canGoNext) {
          next();
        } else {
          correctItemsPosition(itemWidth);
        }
      }
      if (scrollDirection === 'left') {
        const canGoNext = lastX - initialX > minimumTouchDrag;
        if (canGoNext) {
          previous();
        } else {
          correctItemsPosition(itemWidth);
        }
      }
      onMove = false;
      initialX = 0;
      lastX = 0;
      scrollDirection = '';
      initialY = 0;
    }
  };
  return (
    <div className="overflow-hidden relative carousel" ref={containerRef}>
      <ul
        className="z-100 p-0 m-0 flex items-center carousel-track"
        ref={listRef}
        style={{
          transition: 'all .5s',
          transform: `translate3d(${transform}px,0,0)`,
        }}
        onMouseDown={handleDown}
        onMouseMove={handleMove}
        onMouseUp={handleOut}
        onMouseLeave={handleOut}
        onTouchStart={handleDown}
        onTouchMove={handleMove}
        onTouchEnd={handleOut}
        onClick={(e) => {
          if (isDragged) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        {React.Children.toArray(carouseItems).map((child, index) => (
          <li className="list-none" style={{ minWidth: '100%' }} key={index}>
            {child}
          </li>
        ))}
      </ul>

      <div className="arrows-container absolute inset-0 flex items-center justify-between z-10 pointer-events-none">
        <div className="z-100 pointer-events-auto" onClick={previous}>
          {currentSlide === 0 ? arrowLeftDisabled || arrowLeft : arrowLeft}
        </div>
        <div className="z-100 pointer-events-auto" onClick={next}>
          {currentSlide === totalItems - 1 ? arrowRightDisabled || arrowRight : arrowRight}
        </div>
      </div>
      {dots ? (
        <div className="dots flex absolute bottom-0 left-0 w-full right-0 justify-center items-center">
          {new Array(totalItems).fill(0).map((_, index) => (
            <ul
              onClick={() => setCurrentSlide(index)}
              className={`dot-container p-10 cursor-pointer  ${currentSlide === index ? 'dot-container--active' : ''}`}
              key={index}
            >
              <li
                className={`w-8 h-8 rounded-full dot box-content list-none ${
                  currentSlide === index ? 'bg-white dot--active' : 'bg-black hover:opacity-100 opacity-50'
                }`}
              ></li>
            </ul>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default Carousel;
